import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { encryptData } from "../../services/encryption.service";
import { NotificationService } from "../../services/Notification.service";
import { AuthV2Service } from "../../services/authV2Service";
import { useStoreAuthV2 } from "../../store/AuthV2Store";
import { useStoreUserManager } from "../../store/UserManage";
import { useStorePageManager } from "../../store/PageManage";

export const AuthV2OtpPanel = ({
  currentState = 1,
  panelState,
  setPanelState,
  setRequestId,
  setToken,
}) => {
  const { t } = useTranslation();
  const {
    identifier,
    identifierType,
    canResendAt,
    recoveryMode,
    maskedSecondaryContact,
    secondaryContactType,
    isReturningUser,
    prospectId,
    otpAttempts,
    blockedUntil,
    setOtpState,
    setCurrentStep,
    setRecoveryMode,
    incrementOtpAttempts,
    resetOtpAttempts,
    setBlocked,
    isBlocked,
    setTestOtpCode,
    pendingOtpPhone,
    setPendingOtpPhone,
    setFormData,
  } = useStoreAuthV2();

  const OTP_LENGTH = 6;
  const [otpValues, setOtpValues] = useState(Array(OTP_LENGTH).fill(""));
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (!canResendAt) return;
    const updateCountdown = () => {
      const diff = Math.max(
        0,
        Math.floor((new Date(canResendAt) - new Date()) / 1000),
      );
      setCountdown(diff);
    };
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [canResendAt]);

  useEffect(() => {
    if (currentState === 1 && inputRefs.current[0]) {
      setTimeout(() => inputRefs.current[0]?.focus(), 200);
    }
  }, [currentState]);

  const handleInputChange = (index, value) => {
    const digit = value.replace(/[^0-9]/g, "");
    if (digit.length > 1) return;

    const newValues = [...otpValues];
    newValues[index] = digit;
    setOtpValues(newValues);

    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpValues[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/[^0-9]/g, "");
    if (pastedData.length === 0) return;

    const newValues = [...otpValues];
    for (let i = 0; i < OTP_LENGTH; i++) {
      newValues[i] = pastedData[i] || "";
    }
    setOtpValues(newValues);

    const lastIndex = Math.min(pastedData.length, OTP_LENGTH) - 1;
    inputRefs.current[lastIndex]?.focus();
  };

  const getOtpCode = useCallback(() => {
    return otpValues.join("");
  }, [otpValues]);

  const isOtpComplete = () => {
    return otpValues.every((v) => v !== "");
  };

  const handleLoginSuccess = async (result, { keepPanelOpen = false } = {}) => {
    const dataToken = {
      access_token: result.access_token,
      refresh_token: result.refresh_token,
    };
    const encryptToken = await encryptData(dataToken);
    localStorage.setItem("token", encryptToken);

    const dataUserSave = {
      token: result.token || "",
      email: result.email,
    };
    const dataEncrypted = await encryptData(dataUserSave);
    localStorage.setItem("user", dataEncrypted);

    useStoreUserManager.getState().handleUpdateUser({
      id: result.id,
      lastname: result.lastname,
      firstname: result.firstname,
      location: result.location || "",
      connected: true,
      profil: result.profile + `${result.enableHost ? "/hote" : ""}`,
      mail: result.email,
      contact: result.contact || null,
      contactChecked: result.contactActivatedAt && true,
      emailVerified: result.mailActivatedAt,
      primaryIdentifierType: result.contactActivatedAt && !result.mailActivatedAt ? "PHONE" : "EMAIL",
      pieceChecked: result.identityDoc ? 1 : 0,
      identityDoc: result.identityDoc ?? null,
      profilImgUrl: result.avatar ?? null,
      enableHost: result.enableHost || false,
      payment_method_id: result.payment_method_id ?? 0,
    });

    // Afficher le bon message selon le contexte
    const messageKey = recoveryMode
      ? "modals.connexionPanel.authV2.recoveryLoginSuccess"
      : "modals.connexionPanel.authV2.loginSuccess";

    NotificationService.addToQueue(0, t(messageKey));

    setRecoveryMode(false);
    if (!keepPanelOpen) {
      useStorePageManager.getState().handleUpdateConnexionPanelState(false);
    }
  };

  const handleVerify = async () => {
    const code = getOtpCode();
    if (code.length !== OTP_LENGTH || loading) return;

    setLoading(true);
    try {
      let response;
      let result;

      // Mode récupération : utiliser l'endpoint spécial
      if (recoveryMode) {
        response = await AuthV2Service.completeRecovery(prospectId, code);
        result = response.data;

        resetOtpAttempts();

        // Utilisateur sans mot de passe (inscrit par téléphone) → forcer la création d'un mot de passe
        if (result.needsPasswordSetup && result.passwordSetup) {
          await handleLoginSuccess(result, { keepPanelOpen: true });
          setRequestId(result.passwordSetup.requestId);
          setToken(result.passwordSetup.token);
          setPanelState({
            ...panelState,
            authV2Otp: 2,
            newPassword: 1,
          });
          return;
        }

        await handleLoginSuccess(result);

        // Notification pour inviter à définir un contact secondaire
        if (result.needsSecondaryContact) {
          setTimeout(() => {
            NotificationService.addToQueue(
              1,
              t("modals.connexionPanel.authV2.addSecondaryContactReminder"),
            );
          }, 2000);
        }

        return;
      }

      // Mode normal
      response = await AuthV2Service.verifyOtp(identifier, code);
      result = response.data;

      resetOtpAttempts();

      // Returning phone user : connexion automatique après OTP
      if (result.isReturningUser && result.access_token) {
        await handleLoginSuccess(result);
        return;
      }

      if (pendingOtpPhone) {
        setFormData({ secondaryContact: pendingOtpPhone });
        setPendingOtpPhone("");
      }

      setCurrentStep("complete");

      NotificationService.addToQueue(
        0,
        t("modals.connexionPanel.authV2.otpVerified"),
      );

      setPanelState({
        ...panelState,
        authV2Otp: 0,
        authV2Complete: 1,
      });
    } catch (error) {
      incrementOtpAttempts();
      const errorMsg = error.message || "";
      const errorData = error.apiData || error.response?.data || {};

      // Capturer blockedUntil si présent dans l'erreur
      if (errorData.blockedUntil) {
        setBlocked(errorData.blockedUntil);
        console.log('🔒 Compte bloqué jusqu\'à:', errorData.blockedUntil);
      }

      if (errorMsg.includes("expiré") || errorMsg.includes("expired")) {
        NotificationService.addToQueue(
          2,
          t("modals.connexionPanel.authV2.otpExpired"),
        );
      } else if (errorMsg.includes("bloqué") || errorMsg.includes("blocked")) {
        // Calculer le temps de blocage restant
        let blockMessage = t("modals.connexionPanel.authV2.tooManyAttempts");

        if (errorData.blockedUntil) {
          const blockedDate = new Date(errorData.blockedUntil);
          const now = new Date();
          const hoursLeft = Math.ceil((blockedDate - now) / (1000 * 60 * 60));

          blockMessage = `${t("modals.connexionPanel.authV2.accountBlocked")} ${hoursLeft}h. ${t("modals.connexionPanel.authV2.tryAgainLater")}`;
        }

        NotificationService.addToQueue(2, blockMessage);
      } else if (errorMsg.includes("désactivé") || errorMsg.includes("disabled")) {
        NotificationService.addToQueue(
          2,
          errorMsg || "Compte désactivé ! Veuillez l'activer pour continuer",
        );
      } else {
        NotificationService.addToQueue(
          2,
          t("modals.connexionPanel.authV2.otpInvalid"),
        );
      }

      setOtpValues(Array(OTP_LENGTH).fill(""));
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0 || loading) return;

    try {
      const response = await AuthV2Service.resendOtp(identifier);

      setOtpState(
        true,
        new Date(Date.now() + (response.data.cooldownSeconds || 60) * 1000),
        null,
      );
      setTestOtpCode(response.data.otpCode);

      NotificationService.addToQueue(
        0,
        t("modals.connexionPanel.authV2.otpResent"),
      );

      setOtpValues(Array(OTP_LENGTH).fill(""));
      inputRefs.current[0]?.focus();
    } catch (error) {
      NotificationService.addToQueue(
        2,
        error.message || t("modals.connexionPanel.authV2.genericError"),
      );
    }
  };

  const handleBack = () => {
    setOtpValues(Array(OTP_LENGTH).fill(""));

    if (recoveryMode) {
      setRecoveryMode(false);
      setPanelState({
        ...panelState,
        authV2Otp: 2,
        authV2Recovery: 1,
      });
    } else {
      setPanelState({
        ...panelState,
        authV2Otp: 2,
        authV2Entry: 1,
      });
    }
  };

  const handleContactSupport = () => {
    window.open(
      "https://api.whatsapp.com/send?phone=+2250556094636&text=Bonjour%2C%20j%27ai%20besoin%20d%27aide%20pour%20mon%20inscription",
      "_blank",
    );
  };

  const handleNoAccess = () => {
    setOtpValues(Array(OTP_LENGTH).fill(""));
    setPanelState({
      ...panelState,
      authV2Otp: 0,
      authV2Recovery: 1,
    });
  };

  return (
    <div
      className={`contains inscription-part ${
        currentState === 0
          ? "go-left-side"
          : currentState === 1
            ? "come-center"
            : "go-right-side"
      } | flcolm`}
    >
      <button className="back-btn | btn" onClick={handleBack}>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            d="M12.5 15L7.5 10L12.5 5"
            stroke="var(--gray-900)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <label>
        {recoveryMode
          ? secondaryContactType === "email"
            ? t("modals.connexionPanel.authV2.confirmEmail")
            : t("modals.connexionPanel.authV2.confirmPhone")
          : identifierType === "PHONE"
            ? t("modals.connexionPanel.authV2.confirmPhone")
            : t("modals.connexionPanel.authV2.confirmEmail")}
      </label>

      <p className="auth-v2-subtitle">
        {recoveryMode
          ? t("modals.connexionPanel.authV2.otpSubtitle", {
              channel: secondaryContactType === "email" ? "email" : "SMS",
              identifier: maskedSecondaryContact,
            })
          : t("modals.connexionPanel.authV2.otpSubtitle", {
              channel: identifierType === "PHONE" ? "SMS" : "email",
              identifier: identifier,
            })}
      </p>

      {!isBlocked() && (
        <p className="auth-v2-code-validity" style={{ fontSize: "0.875rem", color: "var(--gray-600)", marginTop: "0.5rem" }}>
          {t("modals.connexionPanel.authV2.codeValidFor", { minutes: 5 })}
        </p>
      )}

      <div className="otp-input-group | flrow allcenter">
        {otpValues.map((value, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={value}
            onChange={(e) => handleInputChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={index === 0 ? handlePaste : undefined}
            className="otp-single-input"
            autoComplete="one-time-code"
            disabled={isBlocked()}
          />
        ))}
      </div>

      {/* Compteur de tentatives restantes */}
      {otpAttempts > 0 && otpAttempts < 5 && !isBlocked() && (
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          fontSize: "0.875rem",
          color: "#F59E0B",
          marginTop: "0.5rem",
          fontWeight: "500"
        }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M10 6V10M10 14H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {5 - otpAttempts} {t("modals.connexionPanel.authV2.attemptsRemaining")}
        </div>
      )}

      {/* Message si compte bloqué */}
      {isBlocked() && blockedUntil && (
        <div style={{
          backgroundColor: "#FEE2E2",
          border: "1px solid #FCA5A5",
          borderRadius: "8px",
          padding: "12px",
          marginTop: "1rem"
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", marginBottom: "4px" }}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z" stroke="#991B1B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M4.92896 4.92896L15.071 15.071" stroke="#991B1B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <p style={{ fontSize: "0.875rem", color: "#991B1B", fontWeight: "500", margin: 0 }}>
              {t("modals.connexionPanel.authV2.accountBlockedTitle")}
            </p>
          </div>
          <p style={{ fontSize: "0.75rem", color: "#7F1D1D", margin: 0, textAlign: "center" }}>
            {t("modals.connexionPanel.authV2.accountBlockedMessage", {
              time: new Date(blockedUntil).toLocaleString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              })
            })}
          </p>
        </div>
      )}

      {!isBlocked() && !recoveryMode && (
        <p className="countdown-timer">
          {countdown > 0 ? (
            <>
              {t("modals.connexionPanel.authV2.newCodeIn")}{" "}
              <span className="countdown-value">{countdown}s</span>
            </>
          ) : (
            <span className="resend-link" onClick={handleResend}>
              {t("modals.connexionPanel.authV2.resendCode")}
            </span>
          )}
        </p>
      )}

      {/* Only show recovery link for returning users (not new signups) */}
      {isReturningUser && (
        <span className="recovery-link" onClick={handleNoAccess}>
          {identifierType === "PHONE"
            ? t("modals.connexionPanel.authV2.noAccessPhone")
            : t("modals.connexionPanel.authV2.noAccessEmail")}
        </span>
      )}

      <div className="auth-v2-footer | flrow allcenter">
        <button
          onClick={handleContactSupport}
          className="btn-outline | btn flex"
        >
          {t("modals.connexionPanel.authV2.contactSupport")}
        </button>
        <button
          onClick={handleVerify}
          className="next-btn | btn flex"
          disabled={!isOtpComplete() || loading || isBlocked()}
        >
          {loading
            ? t("modals.connexionPanel.authV2.loading")
            : isBlocked()
            ? t("modals.connexionPanel.authV2.accountBlockedBtn")
            : t("modals.connexionPanel.authV2.validateBtn")}
        </button>
      </div>
    </div>
  );
};
